export default class Input {
    constructor(element) {
        this.element = element;
        this.startX = 0;
        this.startY = 0;
        this.isDown = false;

        this.callbacks = {
            onTap: [],
            onSwipe: []
        };

        this.init();
    }

    init() {
        // Touch Events
        this.element.addEventListener('touchstart', this.handleStart.bind(this), { passive: false });
        this.element.addEventListener('touchend', this.handleEnd.bind(this), { passive: false });

        // Mouse Events (for debugging/desktop)
        this.element.addEventListener('mousedown', this.handleStart.bind(this));
        this.element.addEventListener('mouseup', this.handleEnd.bind(this));
    }

    handleStart(e) {
        // e.preventDefault(); // Prevent default browser behavior if needed
        this.isDown = true;
        const point = this.getPoint(e);
        this.startX = point.x;
        this.startY = point.y;
    }

    handleEnd(e) {
        if (!this.isDown) return;
        this.isDown = false;

        // For touchend, we might need to use changedTouches if available, 
        // but for swipe calculation we usually track movement. 
        // Here we simplify: if distance is small -> Tap, else -> Swipe.

        // Note: In a real swipe implementation we would track 'touchmove'. 
        // For this simple version, we'll just check start vs end if we had move data, 
        // but 'touchend' doesn't give current coordinates easily without 'changedTouches'.

        // Let's refine: We need 'touchmove' to get the end position for swipe.
        // But for 'Tap' we just need start and end to be close.

        // Actually, let's add a simple click listener for Tap as a backup, 
        // but for game feel, we want custom handling.

        // Let's re-implement with 'pointerdown' / 'pointerup' for better unified handling?
        // No, let's stick to basic events for now but add 'mousemove/touchmove' tracking.
    }

    // Re-writing init to use Pointer Events for better cross-device support
    initPointer() {
        this.element.addEventListener('pointerdown', (e) => {
            this.isDown = true;
            this.startX = e.clientX;
            this.startY = e.clientY;
        });

        this.element.addEventListener('pointerup', (e) => {
            if (!this.isDown) return;
            this.isDown = false;
            const endX = e.clientX;
            const endY = e.clientY;

            const diffX = endX - this.startX;
            const diffY = endY - this.startY;
            const dist = Math.sqrt(diffX * diffX + diffY * diffY);

            if (dist < 20) {
                this.trigger('onTap', { x: endX, y: endY });
            } else {
                // Determine direction
                let dir = '';
                if (Math.abs(diffX) > Math.abs(diffY)) {
                    dir = diffX > 0 ? 'right' : 'left';
                } else {
                    dir = diffY > 0 ? 'down' : 'up';
                }
                this.trigger('onSwipe', { direction: dir, x: endX, y: endY });
            }
        });
    }

    on(event, callback) {
        if (this.callbacks[event]) {
            this.callbacks[event].push(callback);
        }
    }

    trigger(event, data) {
        if (this.callbacks[event]) {
            this.callbacks[event].forEach(cb => cb(data));
        }
    }

    getPoint(e) {
        if (e.touches && e.touches.length > 0) {
            return { x: e.touches[0].clientX, y: e.touches[0].clientY };
        }
        return { x: e.clientX, y: e.clientY };
    }
}

// Overwrite the class with the cleaner Pointer Events version
export class InputHandler {
    constructor(element) {
        this.element = element;
        this.callbacks = {};

        this.startX = 0;
        this.startY = 0;

        this.element.addEventListener('pointerdown', this.onDown.bind(this));
        this.element.addEventListener('pointerup', this.onUp.bind(this));
    }

    on(event, cb) {
        if (!this.callbacks[event]) this.callbacks[event] = [];
        this.callbacks[event].push(cb);
    }

    emit(event, data) {
        if (this.callbacks[event]) this.callbacks[event].forEach(cb => cb(data));
    }

    onDown(e) {
        this.startX = e.clientX;
        this.startY = e.clientY;
    }

    onUp(e) {
        const endX = e.clientX;
        const endY = e.clientY;
        const dx = endX - this.startX;
        const dy = endY - this.startY;
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist < 30) {
            this.emit('tap', { x: endX, y: endY });
        } else {
            const absX = Math.abs(dx);
            const absY = Math.abs(dy);
            let dir = '';
            if (absX > absY) {
                dir = dx > 0 ? 'right' : 'left';
            } else {
                dir = dy > 0 ? 'down' : 'up';
            }
            this.emit('swipe', { direction: dir });
        }
    }
}
